import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Award, BookOpen, Check, PlayCircle, Calendar, Languages, ClipboardList, Sunrise, Sparkles, MapPin } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewsSection from "@/components/ReviewsSection";
import LessonsList from "@/components/LessonsList";
import { useBreadcrumbLabel } from "@/contexts/BreadcrumbContext";

function fmtDT(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function CourseDetailPage({ onOpenAuth }) {
    const { id } = useParams();
    const { user } = useAuth();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [gatedLessons, setGatedLessons] = useState(null); // {lessons, access}
    const [sessions, setSessions] = useState([]);
    const [enrolling, setEnrolling] = useState(false);
    const { addItem } = useCart();
    const isAuthed = user && typeof user === "object";

    // Publish dynamic breadcrumb label (Home > Courses > [Title]).
    useBreadcrumbLabel(`/courses/${id}`, course?.title || null);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/courses/${id}`)
            .then(({ data }) => setCourse(data))
            .catch((e) => {
                if (e.response?.status === 404) setNotFound(true);
            })
            .finally(() => setLoading(false));
        api.get(`/courses/${id}/sessions`).then(({ data }) => setSessions(data)).catch(() => setSessions([]));
    }, [id]);

    useEffect(() => {
        if (!isAuthed) { setGatedLessons(null); return; }
        api.get(`/courses/${id}/lessons`)
            .then(({ data }) => setGatedLessons(data))
            .catch(() => setGatedLessons(null));
    }, [id, isAuthed]);

    const accessInfo = gatedLessons?.access;
    const lessons = (gatedLessons?.lessons) || course?.lessons || [];
    const upcomingSessions = sessions.filter((s) => {
        try { return s.status === "scheduled" && new Date(s.end_at) > new Date(); } catch { return false; }
    });

    const handleEnroll = (mode = "online") => {
        if (!course) return;
        if (course.is_membership_only) {
            if (!isAuthed) { onOpenAuth?.(); return; }
            if (!accessInfo?.member) {
                toast.error("This course is members-only — please subscribe to a membership plan.");
                return;
            }
            // free member enrollment
            setEnrolling(true);
            api.post(`/me/enroll-free/${course.id}`)
                .then(() => {
                    toast.success("Enrolled — enjoy the course!");
                    return api.get(`/courses/${id}/lessons`).then(({ data }) => setGatedLessons(data));
                })
                .catch((err) => toast.error(err.response?.data?.detail || err.message))
                .finally(() => setEnrolling(false));
            return;
        }
        const surcharge = mode === "offline" ? (course.offline_surcharge ?? 1500) : 0;
        const price = course.price + surcharge;
        addItem({
            id: course.id,
            kind: "course",
            name: course.title,
            price,
            image_url: course.image_url,
            meta: course.duration,
            mode,
        }, 1);
        toast.success(`Enrolled (${mode === "offline" ? "Offline · Studio" : "Online"})`, {
            description: `${course.title} added to your cart.`,
        });
    };

    return (
        <div className="min-h-screen bg-paper" data-testid="course-detail-page">
            <Navbar onOpenAuth={onOpenAuth} />
            <main>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-brand-600"
                        data-testid="course-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                </div>

                {loading && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="h-96 rounded-3xl bg-gray-100 animate-pulse" />
                    </div>
                )}

                {!loading && notFound && (
                    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
                        <h1 className="font-heading text-3xl font-semibold text-ink-900">
                            Course not found
                        </h1>
                        <p className="mt-3 text-ink-700">
                            The course you're looking for doesn't exist.
                        </p>
                        <Button asChild className="mt-6 rounded-full bg-brand-500 hover:bg-brand-600">
                            <Link to="/">Browse Courses</Link>
                        </Button>
                    </div>
                )}

                {!loading && course && (
                    <>
                        {/* Hero */}
                        <section className="py-12 md:py-16">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
                                <div className="lg:col-span-7 animate-fade-up">
                                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-50 text-accent-600 text-xs font-semibold tracking-wide uppercase">
                                        {course.level}
                                    </span>
                                    <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-heading font-semibold tracking-tight text-ink-900 leading-tight">
                                        {course.title}
                                    </h1>
                                    <p className="mt-5 text-lg text-ink-700 leading-relaxed">
                                        {course.description}
                                    </p>
                                    <div className="mt-6 flex flex-wrap gap-4 text-sm text-ink-700">
                                        <span className="inline-flex items-center gap-1.5">
                                            <Clock className="h-4 w-4 text-brand-500" /> {course.duration}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5">
                                            <Award className="h-4 w-4 text-accent-500" /> Certificate of completion
                                        </span>
                                        <span className="inline-flex items-center gap-1.5">
                                            <BookOpen className="h-4 w-4 text-brand-500" />
                                            {course.lessons?.length || 0} lessons
                                        </span>
                                    </div>

                                    {course.highlights?.length > 0 && (
                                        <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                                            {course.highlights.map((h) => (
                                                <li key={h} className="flex items-start gap-2.5 text-sm text-ink-900">
                                                    <span className="mt-0.5 h-5 w-5 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                                                        <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                                                    </span>
                                                    <span>{h}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {/* Schedule + details */}
                                    {(course.medium || course.start_date || course.end_date || course.total_duration || course.batch_timing || course.next_batch_date) && (
                                        <div className="mt-8 rounded-2xl bg-paper-alt border border-gray-100 p-5" data-testid="course-schedule-card">
                                            <h3 className="font-heading text-xl font-semibold mb-4">Schedule & details</h3>
                                            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                                                {course.medium && (
                                                    <div className="flex items-start gap-2.5" data-testid="course-detail-medium">
                                                        <Languages className="h-4 w-4 mt-0.5 text-brand-600 shrink-0" />
                                                        <div>
                                                            <div className="text-[11px] uppercase tracking-wider text-ink-400">Medium</div>
                                                            <div className="font-medium text-ink-900">{course.medium}</div>
                                                        </div>
                                                    </div>
                                                )}
                                                {(course.start_date || course.end_date) && (
                                                    <div className="flex items-start gap-2.5" data-testid="course-detail-dates">
                                                        <Calendar className="h-4 w-4 mt-0.5 text-brand-600 shrink-0" />
                                                        <div>
                                                            <div className="text-[11px] uppercase tracking-wider text-ink-400">Course dates</div>
                                                            <div className="font-medium text-ink-900">
                                                                {course.start_date || "—"}{course.end_date ? ` → ${course.end_date}` : ""}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {course.total_duration && (
                                                    <div className="flex items-start gap-2.5" data-testid="course-detail-duration">
                                                        <Clock className="h-4 w-4 mt-0.5 text-brand-600 shrink-0" />
                                                        <div>
                                                            <div className="text-[11px] uppercase tracking-wider text-ink-400">Total duration</div>
                                                            <div className="font-medium text-ink-900">{course.total_duration}</div>
                                                        </div>
                                                    </div>
                                                )}
                                                {course.batch_timing && (
                                                    <div className="flex items-start gap-2.5" data-testid="course-detail-batch">
                                                        <Sunrise className="h-4 w-4 mt-0.5 text-accent-500 shrink-0" />
                                                        <div>
                                                            <div className="text-[11px] uppercase tracking-wider text-ink-400">Studio batch (offline)</div>
                                                            <div className="font-medium text-ink-900">{course.batch_timing}</div>
                                                        </div>
                                                    </div>
                                                )}
                                                {course.next_batch_date && (
                                                    <div className="flex items-start gap-2.5" data-testid="course-detail-next-batch">
                                                        <Calendar className="h-4 w-4 mt-0.5 text-accent-500 shrink-0" />
                                                        <div>
                                                            <div className="text-[11px] uppercase tracking-wider text-ink-400">Upcoming batch</div>
                                                            <div className="font-medium text-ink-900">{course.next_batch_date}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {course.guidelines?.length > 0 && (
                                        <div className="mt-8" data-testid="course-guidelines">
                                            <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                                                <ClipboardList className="h-5 w-5 text-brand-600" /> Guidelines
                                            </h3>
                                            <ul className="space-y-2">
                                                {course.guidelines.map((g, i) => (
                                                    <li key={i} className="flex items-start gap-2.5 text-sm text-ink-900" data-testid={`course-guideline-${i}`}>
                                                        <span className="mt-0.5 h-5 w-5 rounded-full bg-accent-50 text-accent-600 flex items-center justify-center shrink-0">
                                                            <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                                                        </span>
                                                        <span>{g}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Side card */}
                                <aside className="lg:col-span-5 lg:sticky lg:top-24 animate-fade-up [animation-delay:120ms]">
                                    <div className="bg-white rounded-3xl shadow-soft border border-gray-100 overflow-hidden">
                                        <div className="aspect-video">
                                            <img
                                                src={course.image_url}
                                                alt={course.title}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="p-6 md:p-7 space-y-5">
                                            <div>
                                                <div className="text-xs uppercase tracking-wider text-ink-400">
                                                    Tuition
                                                </div>
                                                <div className="flex items-baseline gap-2">
                                                    <div className="font-heading text-4xl font-semibold text-ink-900">
                                                        ₹{course.price.toLocaleString("en-IN")}
                                                    </div>
                                                    <div className="text-sm text-ink-700">online</div>
                                                </div>
                                                <div className="mt-1 text-sm text-ink-700">
                                                    Offline (Studio):{" "}
                                                    <span className="font-semibold text-ink-900">
                                                        ₹{(course.price + (course.offline_surcharge ?? 1500)).toLocaleString("en-IN")}
                                                    </span>
                                                    <span className="text-xs text-ink-400 ml-1">
                                                        (+₹{(course.offline_surcharge ?? 1500).toLocaleString("en-IN")})
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="grid gap-2.5">
                                                <Button
                                                    onClick={() => handleEnroll("online")}
                                                    className="w-full rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta py-6 text-base"
                                                    data-testid="course-detail-enroll-online"
                                                >
                                                    Enroll Online
                                                </Button>
                                                {(() => {
                                                    const remaining = course.offline_seats_remaining ?? course.offline_capacity ?? 20;
                                                    const cap = course.offline_capacity ?? 20;
                                                    const soldOut = remaining <= 0;
                                                    const limited = !soldOut && remaining <= Math.max(3, Math.floor(cap * 0.2));
                                                    return (
                                                        <>
                                                            <Button
                                                                onClick={() => handleEnroll("offline")}
                                                                disabled={soldOut}
                                                                variant="outline"
                                                                className="w-full rounded-full border-2 border-accent-400 text-accent-600 hover:bg-accent-50 hover:text-accent-700 hover:border-accent-500 py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                                                                data-testid="course-detail-enroll-offline"
                                                            >
                                                                {soldOut ? "Studio Sold Out" : "Enroll Offline (Studio)"}
                                                            </Button>
                                                            {soldOut && (
                                                                <p className="text-xs text-red-600 text-center font-medium" data-testid="course-detail-soldout-msg">
                                                                    All studio seats are taken for this batch.
                                                                </p>
                                                            )}
                                                            {limited && (
                                                                <p className="text-xs text-accent-600 text-center font-medium" data-testid="course-detail-limited-msg">
                                                                    Only {remaining} seat{remaining === 1 ? "" : "s"} left in the studio
                                                                </p>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                            <p className="text-xs text-ink-700 text-center">
                                                7-day money-back guarantee &middot; Lifetime access to materials
                                            </p>
                                        </div>
                                    </div>
                                </aside>
                            </div>
                        </section>

                        {/* Long Description */}
                        {course.long_description && (
                            <section className="py-10 md:py-14 bg-paper-alt">
                                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                                    <h2 className="font-heading text-3xl font-semibold text-ink-900">
                                        About this course
                                    </h2>
                                    <p className="mt-5 text-base md:text-lg text-ink-700 leading-relaxed">
                                        {course.long_description}
                                    </p>
                                </div>
                            </section>
                        )}

                        {/* Curriculum */}
                        <section className="py-12 md:py-16">
                            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                                <div className="flex flex-wrap items-end justify-between gap-3">
                                    <h2 className="font-heading text-3xl font-semibold text-ink-900">
                                        Curriculum
                                    </h2>
                                    {accessInfo && (
                                        <span className="text-xs uppercase tracking-wider text-ink-400">
                                            {accessInfo.admin ? "Admin view" : accessInfo.enrolled ? "You're enrolled" : accessInfo.member ? "Member access" : "Preview only"}
                                        </span>
                                    )}
                                </div>
                                {lessons?.length > 0 ? (
                                    <LessonsList lessons={lessons} access={accessInfo} />
                                ) : (
                                    <div className="mt-8 bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                                        <p className="text-ink-700">
                                            Detailed curriculum will be shared after enrollment. Reach
                                            out to our team for a syllabus walkthrough.
                                        </p>
                                        <Button asChild variant="outline" className="mt-5 rounded-full border-brand-200 text-brand-700 hover:bg-brand-50">
                                            <Link to="/#contact">Contact us</Link>
                                        </Button>
                                    </div>
                                )}

                                {course.is_membership_only && !accessInfo?.member && !accessInfo?.admin && (
                                    <div className="mt-6 rounded-2xl bg-gradient-to-br from-brand-50 to-accent-50 border border-brand-200 p-5 flex flex-wrap items-center gap-4 justify-between" data-testid="course-members-only-cta">
                                        <div className="flex items-center gap-3">
                                            <Sparkles className="h-5 w-5 text-accent-500" />
                                            <div>
                                                <div className="font-semibold">Members-only course</div>
                                                <div className="text-xs text-ink-700">Subscribe to a membership plan to unlock this course (and more).</div>
                                            </div>
                                        </div>
                                        <Button asChild className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta">
                                            <Link to="/membership">View plans</Link>
                                        </Button>
                                    </div>
                                )}

                                {upcomingSessions.length > 0 && (
                                    <div className="mt-10" data-testid="course-upcoming-sessions">
                                        <h3 className="font-heading text-2xl font-semibold text-ink-900 mb-4 flex items-center gap-2">
                                            <Calendar className="h-5 w-5 text-brand-600" /> Upcoming sessions
                                        </h3>
                                        <ul className="space-y-2">
                                            {upcomingSessions.slice(0, 6).map((s) => (
                                                <li key={s.id} className="rounded-2xl bg-white border border-gray-100 p-4 flex flex-wrap items-start gap-3 justify-between" data-testid={`upcoming-session-${s.id}`}>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className={`text-[11px] uppercase tracking-wide rounded-full px-2 py-0.5 ${s.mode === "online" ? "bg-brand-50 text-brand-700" : "bg-accent-50 text-accent-700"}`}>{s.mode}</span>
                                                            <span className="font-semibold text-sm">{s.title}</span>
                                                        </div>
                                                        <div className="text-xs text-ink-700 mt-1 flex flex-wrap gap-3">
                                                            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDT(s.start_at)}</span>
                                                            {s.instructor && <span>👤 {s.instructor}</span>}
                                                            {s.location_or_link && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.location_or_link}</span>}
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <ReviewsSection itemId={course.id} itemKind="course" onOpenAuth={onOpenAuth} />
                        </section>
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}
